/**
 * Social Go / Pro pricing comparison table.
 * Extracted from LandingPage so the showcase landing and proposal pages share it.
 * Styles live in LandingPage.css (imported globally in App.js).
 */
import { useState } from 'react';
import { CheckCircle, Info, X, Zap } from 'lucide-react';

export const PricingTable = ({ proposal, onNavigate }) => {
  const [beatInfoOpen, setBeatInfoOpen] = useState(false);

  return (
    <section className="lp-section lp-section-green" id="social-go" data-testid="lp-packages">
      <div className="lp-container">
        <h2 className="lp-h2 lp-h2-light">{proposal ? 'Your Pricing' : 'Pricing'}</h2>
        <span className="lp-eyebrow lp-eyebrow-light" style={{ marginBottom: '16px', display: 'block' }}>Choose your bolt-on</span>
        <p className="lp-body-lg lp-body-left lp-body-light lp-body-full">
          Social Go and Social Pro are available exclusively as bolt-ons to the NicheCom visit.
          {!proposal && ' Pricing is bespoke for every agency — speak to your NicheCom representative for a tailored quote.'}
        </p>

        <div className="lp-compare-table" data-testid="lp-compare-table">
          <div className="lp-compare-header">
            <div className="lp-compare-label">Features</div>
            <div className="lp-compare-col">
              <span className="lp-compare-tier">Social Go</span>
              <span className="lp-compare-price">{proposal?.social_go_price || 'POA'}</span>
            </div>
            <div className="lp-compare-col lp-compare-col-pro">
              <span className="lp-compare-tier">Social Pro</span>
              <span className="lp-compare-price">{proposal?.social_pro_price || 'POA'}</span>
              <span className="lp-compare-rec">Recommended</span>
            </div>
          </div>
          {[
            { feature: 'Sneak Peek Video', go: true, pro: true },
            { feature: 'Property Video', go: false, pro: true },
            { feature: 'Social Images (portrait)', go: true, pro: true },
            { feature: 'Beat-Synced Editing', go: true, pro: true, beatInfo: true },
            { feature: 'Logo Branding', go: true, pro: true },
            { feature: 'Script Writing Tool', go: false, pro: true },
            // Inject any custom line items from the proposal so RDs can add bespoke rows
            ...((proposal?.custom_lines || []).map((line) => ({
              feature: line.label,
              note: line.note,
              go: false,
              pro: line.price,
              custom: true,
            }))),
          ].map((row, i) => (
            <div key={i} className={`lp-compare-row ${row.custom ? 'lp-compare-row-custom' : ''}`} data-testid={`lp-compare-row-${i}`}>
              <span className={`lp-compare-feature${row.featureClass ? ' ' + row.featureClass : ''}`}>
                {row.feature}{row.note && <sup>{row.note}</sup>}
                {row.info && (
                  <button className="lp-compare-info-icon" onClick={() => onNavigate && onNavigate(row.info.replace('-info', ''))} data-testid={`lp-info-${row.info}`} title="Learn more">
                    <Info size={13} />
                  </button>
                )}
                {row.beatInfo && (
                  <button className="lp-compare-info-icon" onClick={() => setBeatInfoOpen(true)} data-testid="lp-info-beat-synced" title="How it works">
                    <Info size={13} />
                  </button>
                )}
              </span>
              <span className="lp-compare-val">
                {row.go === true ? <CheckCircle size={22} className="lp-compare-check" /> :
                  row.go === false ? <span className="lp-compare-dash">—</span> : row.go}
              </span>
              <span className="lp-compare-val lp-compare-val-pro">
                {row.pro === true ? <CheckCircle size={22} className="lp-compare-check" /> :
                  row.pro === false ? null : row.pro}
              </span>
            </div>
          ))}

          {/* NarraTour — dark green bolt-on */}
          <div className="lp-compare-row lp-compare-row-addon lp-compare-row-narratour" data-testid="lp-compare-row-narratour">
            <span className="lp-compare-feature lp-compare-feature-addon">
              NarraTour
              <button className="lp-compare-info-icon lp-compare-info-icon-addon" onClick={() => onNavigate && onNavigate('narratour')} data-testid="lp-info-narratour" title="Learn more">
                <Info size={13} />
              </button>
            </span>
            <span className="lp-compare-val lp-compare-val-addon">
              <span className="lp-compare-dash lp-compare-dash-addon">—</span>
            </span>
            <span className="lp-compare-val lp-compare-val-pro lp-compare-val-addon">
              <CheckCircle size={22} className="lp-compare-check lp-compare-check-addon" />
              <span className="lp-compare-poa-badge">{proposal?.narratour_price || 'POA'}</span>
            </span>
          </div>

          {/* PresenTour — magenta bolt-on */}
          <div className="lp-compare-row lp-compare-row-addon lp-compare-row-presentour" data-testid="lp-compare-row-presentour">
            <span className="lp-compare-feature lp-compare-feature-addon">
              PresenTour
              <button className="lp-compare-info-icon lp-compare-info-icon-addon" onClick={() => onNavigate && onNavigate('presentour')} data-testid="lp-info-presentour" title="Learn more">
                <Info size={13} />
              </button>
            </span>
            <span className="lp-compare-val lp-compare-val-addon">
              <span className="lp-compare-dash lp-compare-dash-addon">—</span>
            </span>
            <span className="lp-compare-val lp-compare-val-pro lp-compare-val-addon">
              <CheckCircle size={22} className="lp-compare-check lp-compare-check-addon" />
              <span className="lp-compare-poa-badge">{proposal?.presentour_price || 'POA'}</span>
            </span>
          </div>
        </div>

        {/* Additional Features — separate table */}
        <div className="lp-compare-table lp-compare-table-additional" data-testid="lp-additional-table">
          <div className="lp-compare-subheader lp-compare-subheader-top" data-testid="lp-compare-subheader">Early Access Features</div>

          {/* Property Feedback Video — free Social Pro bonus */}
          <div className="lp-compare-row lp-compare-row-bonus" data-testid="lp-compare-row-feedback-video">
            <span className="lp-compare-feature">
              Property Feedback Video
            </span>
            <span className="lp-compare-val">
              <span className="lp-compare-dash">—</span>
            </span>
            <span className="lp-compare-val lp-compare-val-pro">
              <CheckCircle size={22} className="lp-compare-check" />
              <span className="lp-compare-free-badge">FREE</span>
            </span>
          </div>

          {/* Post Valuation Video — free Social Pro bonus */}
          <div className="lp-compare-row lp-compare-row-bonus" data-testid="lp-compare-row-valuation-video">
            <span className="lp-compare-feature">
              Post Valuation Video
            </span>
            <span className="lp-compare-val">
              <span className="lp-compare-dash">—</span>
            </span>
            <span className="lp-compare-val lp-compare-val-pro">
              <CheckCircle size={22} className="lp-compare-check" />
              <span className="lp-compare-free-badge">FREE</span>
            </span>
          </div>
        </div>

        {/* Beat-Synced Editing info lightbox */}
        {beatInfoOpen && (
          <div className="lp-beat-lightbox-overlay" onClick={() => setBeatInfoOpen(false)} data-testid="beat-info-lightbox">
            <div className="lp-beat-lightbox" onClick={(e) => e.stopPropagation()}>
              <button className="lp-beat-lightbox-close" onClick={() => setBeatInfoOpen(false)} data-testid="beat-info-close" title="Close">
                <X size={18} />
              </button>
              <div className="lp-beat-lightbox-icon">
                <Zap size={22} />
              </div>
              <h3 className="lp-beat-lightbox-title">Beat-Synced Editing</h3>
              <p className="lp-beat-lightbox-lead">
                Every track in our music library has been beat-mapped by AI audio analysis —
                every drop, build and beat tracked and time-stamped.
              </p>
              <p className="lp-beat-lightbox-body">
                When we cut your Sneak Peeks and Property Videos, each edit lands precisely on
                the beat — so rooms reveal in rhythm and the drop hits just as the property
                shows its best side.
              </p>
              <ul className="lp-beat-lightbox-list">
                <li><CheckCircle size={16} /> Scroll-stopping pace, built for Reels &amp; TikTok</li>
                <li><CheckCircle size={16} /> Every video, every listing — perfectly timed</li>
                <li><CheckCircle size={16} /> AI handles the timing, editors focus on the story</li>
              </ul>
            </div>
          </div>
        )}

        {proposal?.notes && (
          <div className="lp-proposal-notes" data-testid="lp-proposal-notes">
            <span className="lp-proposal-notes-label">Notes</span>
            <p>{proposal.notes}</p>
          </div>
        )}

        {!proposal && (
          <div className="lp-poa-cta" data-testid="lp-poa-cta">
            <strong>Price on application</strong>
            <span>Every agency is different, so every quote is bespoke. Speak to your NicheCom representative for a tailored proposal — or drop us a line and we'll have one ready inside 24 hours.</span>
            <a className="lp-poa-cta-btn" href="mailto:hello@nichecom.co.uk?subject=NicheCom%20Social%20Tools%20—%20Pricing%20Enquiry" data-testid="lp-poa-cta-btn">
              Get in touch
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default PricingTable;
